import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Activity, TrendingUp, TrendingDown, Users, 
    AlertTriangle, Map, Star, ArrowRight, Loader2, Calendar 
} from 'lucide-react';

const AdvancedReports = () => {
    const API_URL = process.env.REACT_APP_API_URL;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            try {
                const res = await axios.get(`${API_URL}/api/admin/advanced-reports`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setData(res.data);
            } catch (error) {
                console.error("Veri hatası", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Grafik için maksimum değeri bul (Barların yüksekliğini ayarlamak için)
    const maxGraphValue = data ? Math.max(...data.activity_graph.map(d => d.count), 1) : 10;

    if (loading) return (
        <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-gray-500">
            <Loader2 className="animate-spin w-12 h-12 mb-4 text-indigo-500" />
            <span className="text-lg font-medium">İstatistikler analiz ediliyor...</span>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 p-6 lg:p-12 font-sans">
            
            {/* --- HEADER --- */}
            <div className="max-w-7xl mx-auto mb-10 flex flex-col md:flex-row justify-between items-end border-b border-gray-800 pb-6">
                <div>
                    <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">
                        Detaylı Analiz Raporu
                    </h1>
                    <p className="text-gray-400 mt-2 text-sm">
                        Sistem aktivitesi, rota performansı ve kullanıcı eğilimleri.
                    </p>
                </div>
                <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg border border-gray-700 text-xs text-gray-400">
                    <Calendar size={14} />
                    <span>Son 7 Gün Verileri</span>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* --- 1. KPI KARTI (SOL ÜST) --- */}
                <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 shadow-xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Activity size={100} className="text-indigo-500" />
                    </div>
                    <h3 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">Haftalık Aktif Kullanıcı</h3>
                    <div className="flex items-end gap-3">
                        <span className="text-4xl font-extrabold text-white">{data.kpi.active_users_weekly}</span>
                        <div className={`flex items-center text-sm font-bold px-2 py-1 rounded mb-1 ${
                            data.kpi.trend_direction === 'up' 
                                ? 'bg-green-500/20 text-green-400' 
                                : data.kpi.trend_direction === 'down' ? 'bg-red-500/20 text-red-400' : 'bg-gray-700 text-gray-400'
                        }`}>
                            {data.kpi.trend_direction === 'up' ? <TrendingUp size={14} className="mr-1" /> : <TrendingDown size={14} className="mr-1" />}
                            %{Math.abs(data.kpi.trend_percent)}
                        </div>
                    </div>
                    <p className="text-gray-500 text-xs mt-4">
                        Geçen haftaya göre AI tahmin servisini kullanan tekil kullanıcı değişimi.
                    </p>
                </div>

                {/* --- 2. GRAFİK KARTI (ORTA & SAĞ ÜST - 2 SPAN) --- */}
                <div className="lg:col-span-2 bg-gray-800 rounded-2xl p-6 border border-gray-700 shadow-xl flex flex-col justify-between">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-lg text-white flex items-center gap-2">
                            <Activity className="text-cyan-400" size={20} /> Günlük Tahmin Aktivitesi
                        </h3>
                    </div>
                    
                    {/* CSS Bar Chart */}
                    <div className="flex items-end justify-between gap-2 h-40 w-full">
                        {data.activity_graph.map((item, index) => {
                            // Bar yüksekliği hesapla (min %10 olsun ki görünsün)
                            const heightPercent = Math.max((item.count / maxGraphValue) * 100, 10);
                            return (
                                <div key={index} className="flex-1 flex flex-col items-center group">
                                    <div className="relative w-full flex items-end justify-center h-full">
                                        <div 
                                            className="w-full max-w-[40px] bg-indigo-600 rounded-t-md group-hover:bg-cyan-400 transition-all duration-300 relative"
                                            style={{ height: `${heightPercent}%` }}
                                        >
                                            {/* Tooltip */}
                                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity border border-gray-600 whitespace-nowrap z-10">
                                                {item.count} İşlem
                                            </div>
                                        </div>
                                    </div>
                                    <span className="text-xs text-gray-500 mt-3 font-medium">{item.day}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* --- 3. ANALİZ TABLOSU: SORUNLU ROTALAR (SOL ALT - 2 SPAN) --- */}
                <div className="lg:col-span-2 bg-gray-800 rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
                    <div className="p-6 border-b border-gray-700 bg-gray-800/50 flex justify-between items-center">
                        <h3 className="font-bold text-lg text-white flex items-center gap-2">
                            <AlertTriangle className="text-orange-400" size={20} /> İyileştirilmesi Gereken Rotalar
                        </h3>
                        <span className="text-xs text-gray-500">Yarıda bırakılma oranına göre</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-gray-400">
                            <thead className="bg-gray-900/30 text-xs uppercase font-bold text-gray-500">
                                <tr>
                                    <th className="px-6 py-3">Rota</th>
                                    <th className="px-6 py-3 text-center">Başlayan</th>
                                    <th className="px-6 py-3 text-center">Bitiren</th>
                                    <th className="px-6 py-3 text-right">Terk Edilme</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/50">
                                {data.problematic_routes.length > 0 ? (
                                    data.problematic_routes.map((route, idx) => (
                                        <tr key={idx} className="hover:bg-gray-700/20 transition-colors">
                                            <td className="px-6 py-4 font-medium text-white flex items-center gap-2">
                                                <Map size={16} className="text-gray-500" />
                                                {route.title}
                                            </td>
                                            <td className="px-6 py-4 text-center">{route.started}</td>
                                            <td className="px-6 py-4 text-center">{route.completed}</td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <div className="w-24 bg-gray-700 rounded-full h-2 overflow-hidden">
                                                        <div 
                                                            className="bg-orange-500 h-full rounded-full" 
                                                            style={{ width: `${route.drop_off}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="font-bold text-orange-400 w-8">%{route.drop_off}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="p-8 text-center text-gray-500">
                                            Harika! Yüksek oranda terk edilen rota bulunmuyor.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* --- 4. ANALİZ LİSTESİ: EN SEVİLEN YERLER (SAĞ ALT) --- */}
                <div className="bg-gray-800 rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
                    <div className="p-6 border-b border-gray-700 bg-gray-800/50">
                        <h3 className="font-bold text-lg text-white flex items-center gap-2">
                            <Star className="text-yellow-400" size={20} /> En Beğenilen Yerler
                        </h3>
                    </div>
                    <div className="divide-y divide-gray-700/50">
                        {data.top_places.map((place, idx) => (
                            <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-700/20 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center font-bold text-gray-400 text-xs">
                                        #{idx + 1}
                                    </div>
                                    <div>
                                        <p className="font-bold text-gray-200 text-sm">{place.place}</p>
                                        <p className="text-xs text-gray-500">{place.votes} Oy</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 bg-yellow-400/10 px-2 py-1 rounded text-yellow-400 font-bold text-sm">
                                    <Star size={12} fill="currentColor" />
                                    {place.score}
                                </div>
                            </div>
                        ))}
                         {data.top_places.length === 0 && (
                             <div className="p-6 text-center text-gray-500 text-sm">Henüz puanlama verisi yok.</div>
                         )}
                    </div>
                    <div className="p-4 bg-gray-900/30 border-t border-gray-700 text-center">
                        <button className="text-xs text-indigo-400 hover:text-white font-medium flex items-center justify-center gap-1 w-full transition-colors">
                            Tüm Mekanları Gör <ArrowRight size={12} />
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdvancedReports;